import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: string;
  createdAt: string;
}

export default function TenantsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [items, setItems] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const tableBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const separator = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const hoverBg = isDark ? 'rgba(255,255,255,0.04)' : '#F9F9FB';
  const iconBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const selectBg = isDark ? '#1A1A1A' : '#fff';

  useEffect(() => { loadTenants(); }, []);

  const loadTenants = async () => {
    setLoading(true);
    try {
      const data = await api.get<Tenant[]>('/tenants');
      setItems(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setItems([]);
      else addToast(getApiErrorMessage(err, t('adm_err_tenants')), 'error');
    } finally { setLoading(false); }
  };

  const filtered = items.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()) || t.slug.toLowerCase().includes(search.toLowerCase()));

  const getStatusBadge = (status: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      active: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
      inactive: { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
      suspended: { bg: 'color-mix(in srgb, var(--warn) 12%, transparent)', color: 'var(--warn)' },
      pending: { bg: 'rgba(184,134,11,0.1)', color: '#B8860B' },
    };
    const style = map[status] ?? map.inactive;
    return <span className="text-xs font-medium px-2.5 py-1 rounded-full capitalize" style={{ background: style.bg, color: style.color }}>{status}</span>;
  };

  return (
    <DashboardLayout breadcrumb={[t('adm_section'), t('adm_tenants')]}>
      <div className="mb-6 flex items-center justify-between">
        <div><h1 className="text-2xl font-bold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>{t('adm_tenants')}</h1><p className="text-sm mt-1" style={{ color: textMuted }}>{t('adm_manage_tenants')}</p></div>
      </div>
      <div className="rounded-xl" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        <div className="flex items-center gap-3 p-4" style={{ borderBottom: `1px solid ${separator}` }}>
          <div className="flex items-center gap-2 px-3 py-2 rounded-md flex-1 max-w-md" style={{ background: inputBg, border: `1px solid ${inputBorder}` }}>
            <i className="ri-search-line text-sm" style={{ color: textMuted }}></i>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or slug..." className="bg-transparent outline-none text-sm flex-1" style={{ color: textMain }} />
          </div>
          <button onClick={loadTenants} className="w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-colors" style={{ border: `1px solid ${inputBorder}`, background: selectBg }} title="Refresh">
            <i className="ri-refresh-line text-sm" style={{ color: textMuted }}></i>
          </button>
        </div>
        {loading ? (
          <div className="p-12 text-center"><i className="ri-loader-4-line animate-spin text-2xl" style={{ color: textMuted }}></i><p className="text-sm mt-3" style={{ color: textMuted }}>Loading...</p></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center"><div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}><i className="ri-building-4-line text-xl" style={{ color: textMuted }}></i></div><p className="text-sm font-medium" style={{ color: textMain }}>{t('adm_no_tenants')}</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm"><thead><tr style={{ background: tableHeaderBg }}><th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>Name</th><th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('common_slug')}</th><th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>Status</th><th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('common_created')}</th><th className="px-4 py-3"></th></tr></thead>
            <tbody>
              {filtered.map((tenant) => (
                <tr key={tenant.id} className="cursor-pointer transition-colors" onClick={() => navigate(`/admin/tenants/${tenant.id}`)} style={{ borderBottom: `1px solid ${tableBorder}` }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                  <td className="px-4 py-3"><div className="font-medium" style={{ color: textMain }}>{tenant.name}</div></td>
                  <td className="px-4 py-3 font-mono text-xs" style={{ color: textMuted }}>{tenant.slug}</td>
                  <td className="px-4 py-3">{getStatusBadge(tenant.status)}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: textMuted }}>{new Date(tenant.createdAt).toLocaleDateString('uk-UA')}</td>
                  <td className="px-4 py-3"><button className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer transition-colors"
                    onMouseEnter={(e) => { e.currentTarget.style.background = iconBg; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                    <i className="ri-arrow-right-s-line" style={{ color: textMuted }}></i></button></td>
                </tr>
              ))}
            </tbody></table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}